import React, { useEffect, useRef, useState } from 'react';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { Trash, ChevronDown, ChevronUp } from 'lucide-react';
import { toast } from 'sonner';
import { Download } from 'lucide-react';
import Page from 'components/shared/Page';
import HistoricalCampaignStrip from 'components/shared/HistoricalCampaignStrip';
import { useAGLOCampaign, useCampaignSwitchStatus } from 'hooks/useAGLOCampaign';
import ConfirmationModal from 'components/shared/ConfirmationModal';
import EmptyCard from 'components/shared/EmptyCard';
import CategoryBadge from 'components/shared/CategoryBadge';
import { LockableButton } from 'components/shared/Lockable';
import { useAPI, useGetPilgrim, useGetUser, useDebounce } from 'hooks';
import { formatNumber, getUserName, preventSubmitWithENTER } from 'helpers';
import Locales from 'locales';
import { FullPaymentIcon, PartialPaymentIcon, NpiCheckSuccessIcon, NpiCheckFailIcon, NpiCheckPendingIcon } from 'data';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow, TableFooter } from 'components/ui/table';
import { Badge } from 'components/ui/badge';
import { Tooltip, TooltipContent, TooltipTrigger } from 'components/ui/tooltip';
import { Button } from 'components/ui/button';
import { Input } from 'components/ui/input';
import { Select, SelectTrigger, SelectValue, SelectContent, SelectItem } from 'components/ui/select';

const Group = () => {
  const { api } = useAPI();
  const queryClient = useQueryClient();
  const { isAgency } = useGetUser();
  const {
    isAGLO,
    selectedCampaignYear,
    params: campaignParams,
    scope: campaignScope,
    isReadOnly,
    isCampaignSwitching,
  } = useAGLOCampaign();
  const [openDeleteGroupModal, setOpenDeleteGroupModal] = useState(false);
  const [openDeleteGroupMemberModal, setOpenDeleteGroupMemberModal] = useState(false);
  const [selectedGroup, setSelectedGroup] = useState(null);
  const [selectedGroupMember, setSelectedGroupMember] = useState(null);
  const [pageByGroup, setPageByGroup] = useState({});
  const [expandedGroups, setExpandedGroups] = useState({});
  const [search, setSearch] = useState('');
  const [selectedAgency, setSelectedAgency] = useState('all');
  const previousCampaignYear = useRef(selectedCampaignYear);
  useEffect(() => {
    if (!isAGLO || previousCampaignYear.current === selectedCampaignYear) return;
    previousCampaignYear.current = selectedCampaignYear;
    setPageByGroup({});
    setExpandedGroups({});
    setSelectedGroup(null);
    setSelectedGroupMember(null);
    setOpenDeleteGroupModal(false);
    setOpenDeleteGroupMemberModal(false);
  }, [isAGLO, selectedCampaignYear]);
  const debouncedSearchQuery = useDebounce(search, 1000);
  const perPage = 30;

  const groupsQuery = useQuery({
    queryFn: api.list('trip/', { group_by: 'agency_and_group', ...campaignParams }),
    queryKey: isAGLO
      ? ['trip/groupes/', { group_by: 'agency_and_group', ...campaignParams }, ...campaignScope]
      : ['trip/groupes/'],
    keepPreviousData: !isAGLO,
    ...(isAGLO && isCampaignSwitching ? { staleTime: 0 } : {}),
  });

  const { data: groupsData } = groupsQuery;
  useCampaignSwitchStatus([groupsQuery]);

  // only display groups.
  // group_id = null when there are no group
  const groups = groupsData
    ? groupsData.data
        .map((agency) => ({
          ...agency,
          groups: agency.groups.filter((g) => g.group_id !== null),
        }))
        .filter((agency) => agency.groups.length > 0)
    : [];

  const filteredGroups = groups
    ? groups.filter((g) => {
        if (selectedAgency === 'all') return true;
        return String(g.agency_id) === selectedAgency;
      })
    : [];

  const { mutate: deleteGroup } = useMutation({
    mutationFn: (group) => api.remove('agency/groups', group.group_id),
    onSuccess: (_, deletedGroup) => {
      queryClient.invalidateQueries({ queryKey: ['trip/'] });
      queryClient.invalidateQueries({ queryKey: ['trip/groupes/'] });
      toast.success(Locales.formatString(Locales.trip.toast.group_delete, { name: deletedGroup?.group_name }));
      setSelectedGroup(null);
    },
  });

  const { mutate: deleteGroupMember } = useMutation({
    mutationFn: (groupMember) =>
      api.post(`agency/groups/${groupMember.group.group_id}/remove_trip/${groupMember.trip.id}/`),
    onSuccess: (_, deletedGroupMember) => {
      queryClient.invalidateQueries({ queryKey: ['trip/'] });
      queryClient.invalidateQueries({ queryKey: ['trip/groupes/'] });
      toast.success(
        Locales.formatString(Locales.trip.toast.group_member_delete, {
          pilgrimName: getUserName(
            deletedGroupMember?.trip.pilgrim.first_name,
            deletedGroupMember?.trip.pilgrim.last_name
          ),
          groupName: deletedGroupMember?.group.group_name,
        })
      );
      setSelectedGroupMember(null);
    },
  });

  const { mutate: generateMedicalCertificate } = useMutation({
    mutationFn: ({ id }) => api.get(`trip/${id}/generate_medical_certificate/`, { responseType: 'blob' }),
    onSuccess: ({ data }, { pilgrimData }) => {
      const fileName = `certificat_medicale_de_${getUserName(
        pilgrimData?.pilgrim?.first_name,
        pilgrimData?.pilgrim?.last_name
      )}_${pilgrimData?.pilgrim?.passport || pilgrimData?.pilgrim?.npi}.pdf`;

      // Create a URL for the blob object
      const url = window.URL.createObjectURL(new Blob([data]));
      const link = document.createElement('a');
      link.href = url;
      link.setAttribute('download', fileName);
      document.body.appendChild(link);
      link.click();

      // Clean up and remove the link
      link.parentNode.removeChild(link);
      window.URL.revokeObjectURL(url);
    },
  });

  const handleGenerateMedicalCertificate = (pilgrimData) => {
    if (isReadOnly) return;
    generateMedicalCertificate({ id: pilgrimData.id, pilgrimData });
  };

  const handleDeleteGroup = (group) => {
    setOpenDeleteGroupModal(true);
    setSelectedGroup(group);
  };

  const handleDeleteGroupMember = (group, trip) => {
    setOpenDeleteGroupMemberModal(true);
    setSelectedGroupMember({ group, trip });
  };

  const paginate = (array, page, perPage) => {
    const start = (page - 1) * perPage;
    return array.slice(start, start + perPage);
  };

  const getPage = (groupId) => pageByGroup[groupId] || 1;

  const setGroupPage = (groupId, newPage) => {
    setPageByGroup((prev) => ({ ...prev, [groupId]: newPage }));
  };

  const toggleGroup = (groupId) => {
    setExpandedGroups((prev) => ({
      ...prev,
      [groupId]: !prev[groupId],
    }));
  };

  return (
    <Page
      title="Groupements de pèlerins"
      headerContent={<HistoricalCampaignStrip />}
      description="Liste des groupements de pèlerins"
      className="bg-white"
      removeContentStyles
    >
      {(!groups || groups.length === 0) && (
        <EmptyCard className="bg-white rounded-sm border border-solid border-gray-200" />
      )}

      {groupsData && (groups.length > 0 || selectedAgency !== 'all') && (
        <>
          <div className="flex gap-2 mb-4 rounded-sm border border-gray-200 p-4">
            {groups.length > 0 && (
              <Input
                placeholder="Rechercher un pèlerin (Nom, Prénoms, NPI, Passeport)"
                className="w-full rounded-sm"
                onChange={(e) => setSearch(e.target.value)}
                onKeyDown={preventSubmitWithENTER}
              />
            )}

            {!isAgency && (
              <Select value={selectedAgency} onValueChange={setSelectedAgency}>
                <SelectTrigger className="w-[200px]">
                  <SelectValue placeholder="Filtrer par agence" />
                </SelectTrigger>

                <SelectContent>
                  <SelectItem value="all">Toutes les agences</SelectItem>

                  {selectedAgency !== 'all' && !groups.some((g) => String(g.agency_id) === selectedAgency) && (
                    <SelectItem value={selectedAgency}>Société {selectedAgency} (absente de cette campagne)</SelectItem>
                  )}
                  {groups.map((g) => (
                    <SelectItem key={g.agency_id} value={String(g.agency_id)}>
                      {g.agency_name}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            )}
          </div>

          <div className="flex flex-col gap-4">
            {filteredGroups.map((data) => (
              <div key={data.agency_id} className="flex flex-col gap-4">
                {data.groups.map((group) => {
                  const agencyName = data.agency_name;
                  const page = getPage(group.group_id);

                  const filteredTrips = group.trips.filter((trip) => {
                    const { firstname, lastname, npi, passport } = useGetPilgrim(trip);
                    const q = debouncedSearchQuery.toLowerCase();

                    return (
                      firstname?.toLowerCase().includes(q) ||
                      lastname?.toLowerCase().includes(q) ||
                      npi?.toLowerCase().includes(q) ||
                      passport?.toLowerCase().includes(q)
                    );
                  });

                  //  Hide group when search returns no result
                  if (debouncedSearchQuery && filteredTrips.length === 0) {
                    return null;
                  }

                  const memberCount = filteredTrips.length;
                  const paginatedTrips = paginate(filteredTrips, page, perPage);
                  const totalPages = Math.ceil(memberCount / perPage);
                  const isDisplayedGroup = expandedGroups[group.group_id];
                  const isGroupCreatedByAgency =
                    isAgency && filteredTrips && filteredTrips.every((trip) => !trip.self_registered);

                  return (
                    <div key={group.group_id} className="rounded-sm border border-gray-200 p-4">
                      <div className="flex gap-4 justify-between items-center mb-4">
                        <div className="flex gap-[30px]">
                          <h2 className="text-lg">
                            Groupe <span className="text-hajj-primary font-bold">{group.group_name}</span>
                          </h2>

                          {!isAgency && <Badge variant="outline">{`Agence : ${agencyName}`}</Badge>}

                          <span className="text-gray-500">{`${memberCount} pèlerins`}</span>
                        </div>

                        <div className="space-x-2">
                          {isGroupCreatedByAgency && (
                            <LockableButton variant="outline" onClick={() => handleDeleteGroup(group)}>
                              <Trash />
                              Supprimer le groupe
                            </LockableButton>
                          )}

                          <Button variant="outline" size="sm" onClick={() => toggleGroup(group.group_id)}>
                            {isDisplayedGroup ? <ChevronUp /> : <ChevronDown />}

                            {isDisplayedGroup ? 'Masquer' : 'Afficher'}
                          </Button>
                        </div>
                      </div>

                      {isDisplayedGroup && (
                        <div className="rounded-sm overflow-hidden border border-gray-200">
                          <Table>
                            <TableHeader>
                              <TableHead>NPI</TableHead>
                              <TableHead>Nom</TableHead>
                              <TableHead>Prénoms</TableHead>
                              <TableHead>Passeport</TableHead>
                              <TableHead>Catégorie</TableHead>
                              <TableHead>Santé</TableHead>
                              <TableHead>Montant payé</TableHead>
                              {isGroupCreatedByAgency && <TableHead>Actions</TableHead>}
                            </TableHeader>

                            <TableBody>
                              {paginatedTrips.map((trip) => {
                                const {
                                  id,
                                  firstname,
                                  lastname,
                                  npi,
                                  isNpiValid,
                                  passport,
                                  categoryName,
                                  isHealthy,
                                  depositTransferred,
                                  isInvoiceFullyPaid,
                                  isPartiallyPaid,
                                } = useGetPilgrim(trip);

                                return (
                                  <TableRow key={id}>
                                    <TableCell>
                                      <div className="flex items-center gap-2">
                                        {npi || '-'}

                                        {isNpiValid && <NpiCheckSuccessIcon />}

                                        {isNpiValid === false && <NpiCheckFailIcon />}

                                        {isNpiValid === null && <NpiCheckPendingIcon />}
                                      </div>
                                    </TableCell>
                                    <TableCell className="font-medium">{lastname}</TableCell>
                                    <TableCell className="font-medium">{firstname}</TableCell>

                                    <TableCell>{passport || '-'}</TableCell>

                                    <TableCell>
                                      <CategoryBadge categoryName={categoryName} />
                                    </TableCell>

                                    <TableCell>
                                      {isHealthy === null && <Badge variant="pending">En attente</Badge>}

                                      {isHealthy !== null && (
                                        <Tooltip>
                                          <TooltipTrigger asChild>
                                            <Badge
                                              variant={isHealthy ? 'success' : 'destructive'}
                                              className={isReadOnly ? 'gap-1' : 'cursor-pointer gap-1'}
                                              onClick={
                                                isReadOnly ? undefined : () => handleGenerateMedicalCertificate(trip)
                                              }
                                            >
                                              {!isReadOnly && <Download size={15} />}
                                              {isHealthy ? 'Apte' : 'Inapte'}
                                            </Badge>
                                          </TooltipTrigger>
                                          <TooltipContent>
                                            <p>
                                              {isReadOnly ? 'Visite médicale' : 'Télécharger votre certificat médicale'}
                                            </p>
                                          </TooltipContent>
                                        </Tooltip>
                                      )}
                                    </TableCell>
                                    <TableCell>
                                      <div className="flex items-center gap-2">
                                        {formatNumber(depositTransferred)}

                                        {isInvoiceFullyPaid && <FullPaymentIcon />}

                                        {isPartiallyPaid && <PartialPaymentIcon />}
                                      </div>
                                    </TableCell>

                                    {isGroupCreatedByAgency && (
                                      <TableCell>
                                        <Tooltip>
                                          <TooltipTrigger asChild>
                                            <LockableButton
                                              variant="outline"
                                              size="icon"
                                              onClick={() => handleDeleteGroupMember(group, trip)}
                                            >
                                              <Trash />
                                            </LockableButton>
                                          </TooltipTrigger>
                                          <TooltipContent>
                                            <p>Supprimer le pèlerin du groupe</p>
                                          </TooltipContent>
                                        </Tooltip>
                                      </TableCell>
                                    )}
                                  </TableRow>
                                );
                              })}
                            </TableBody>

                            {totalPages > 1 && (
                              <TableFooter>
                                <TableRow>
                                  <TableCell colSpan={isGroupCreatedByAgency ? 8 : 7}>
                                    <div className="flex items-center justify-between mt-6 text-sm text-gray-600 ">
                                      <p className="font-medium">
                                        Page {page} sur {totalPages}
                                      </p>

                                      <div className="flex gap-2">
                                        <Button
                                          variant="outline"
                                          size="sm"
                                          className="rounded-lg"
                                          disabled={page === 1}
                                          onClick={() => setGroupPage(group.group_id, page - 1)}
                                        >
                                          Précédent
                                        </Button>

                                        <Button
                                          variant="outline"
                                          size="sm"
                                          className="rounded-lg"
                                          disabled={page >= totalPages}
                                          onClick={() => setGroupPage(group.group_id, page + 1)}
                                        >
                                          Suivant
                                        </Button>
                                      </div>
                                    </div>
                                  </TableCell>
                                </TableRow>
                              </TableFooter>
                            )}
                          </Table>
                        </div>
                      )}
                    </div>
                  );
                })}
              </div>
            ))}
          </div>
        </>
      )}

      {selectedGroup && (
        <ConfirmationModal
          isOpen={openDeleteGroupModal}
          setOpenModal={setOpenDeleteGroupModal}
          title="Supprimer le groupement de pèlerins"
          confirmBtnVariant="destructive"
          confirmBtnLabel={Locales.base.button.delete}
          message={
            <p>
              {Locales.formatString(Locales.trip.modal.text.delete_group, {
                name: <strong>{selectedGroup.group_name}</strong>,
              })}
            </p>
          }
          onConfirm={() => deleteGroup(selectedGroup)}
        />
      )}

      {selectedGroupMember && (
        <ConfirmationModal
          isOpen={openDeleteGroupMemberModal}
          setOpenModal={setOpenDeleteGroupMemberModal}
          title="Supprimer le pèlerin du groupement"
          confirmBtnVariant="destructive"
          confirmBtnLabel={Locales.base.button.delete}
          message={
            <p>
              {Locales.formatString(Locales.trip.modal.text.delete_group_member, {
                pilgrimName: (
                  <strong>
                    {getUserName(
                      selectedGroupMember?.trip.pilgrim.first_name,
                      selectedGroupMember?.trip.pilgrim.last_name
                    )}
                  </strong>
                ),
                groupName: <strong>{selectedGroupMember.group.group_name}</strong>,
              })}
            </p>
          }
          onConfirm={() => deleteGroupMember(selectedGroupMember)}
        />
      )}
    </Page>
  );
};

export default Group;
